import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ScrollToHashElementProps {
   /** On first run do we jump or scroll? */
   initialBehavior?: ScrollBehavior;
   behavior?: ScrollBehavior;
   inline?: ScrollLogicalPosition;
   block?: ScrollLogicalPosition;
}

const ScrollToHashElement = ({
   behavior = 'auto',
   initialBehavior = 'auto',
   inline = 'nearest',
   block = 'start',
}: ScrollToHashElementProps): null => {
   const [hash, setHash] = useState(window.location.hash);
   const [count, setCount] = useState(0);

   // We need to know if this is the first run. If it is, we can do an instant jump, no scrolling.
   const isFirstRunRef = useRef(true);

   useEffect(() => {
      const handleLocationChange = () => {
         setHash(window.location.hash);

         // We increment count just so the layout effect will run if the hash is the same.
         // Otherwise the user might click a hashlink a second time and it won't go anywhere.
         setCount((count: number) => count + 1);
      };

      const onPopState = () => {
         window.dispatchEvent(new Event('locationchange'));
      };

      // Annotate with `this: void` to avoid unintentional scoping of `this`
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const originalPushState = window.history.pushState;
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const originalReplaceState = window.history.replaceState;

      const addWindowListeners = () => {
         window.history.pushState = function (...args: any) {
            const result = originalPushState.apply(this, args);
            window.dispatchEvent(new Event('pushstate'));
            window.dispatchEvent(new Event('locationchange'));
            return result;
         };

         window.history.replaceState = function (...args: any) {
            const result = originalReplaceState.apply(this, args);
            window.dispatchEvent(new Event('replacestate'));
            window.dispatchEvent(new Event('locationchange'));
            return result;
         };

         window.addEventListener('popstate', onPopState);
         window.addEventListener('locationchange', handleLocationChange);
      };

      // Cleanup the event listeners on component unmount
      const removeWindowListeners = () => {
         window.history.pushState = originalPushState;
         window.history.replaceState = originalReplaceState;
         window.removeEventListener('popstate', onPopState);
         window.removeEventListener('locationchange', handleLocationChange);
      };

      addWindowListeners();
      return removeWindowListeners;
   }, []);

   useLayoutEffect(() => {
      const removeHashCharacter = (str: string) => {
         const result = str.slice(1);
         return result;
      };

      if (hash) {
         const element = document.getElementById(removeHashCharacter(hash));

         if (element) {
            element.scrollIntoView({
               behavior: isFirstRunRef.current ? initialBehavior : behavior,
               inline: inline,
               block: block,
            });
         }
      }

      isFirstRunRef.current = false;
   }, [hash, count, initialBehavior, behavior, inline, block]);

   return null;
};

export default ScrollToHashElement;
